import React, { Suspense, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { FiAlertTriangle } from '@react-icons/all-files/fi/FiAlertTriangle'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { FiChevronUp } from '@react-icons/all-files/fi/FiChevronUp'
import { FiShoppingCart } from '@react-icons/all-files/fi/FiShoppingCart'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import { helper } from '../../../utilities/helper'
import { getWorkingPackage } from '../../../store/slices/entities/packages'
import { getUser, getUserGeo, purchaseAddItem } from '../../../store/slices/user'
import { getWorkingOrder } from '../../../store/slices/entities/orders'
import { Spinner } from '../../Components'
import * as gtag from '../../../lib/gtag'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import { MEDIA_POLICY_KEYS } from '../primitives/mediaPolicy'
import styles from './ProductRow.module.scss'
import { getBranchAvailability, requiresMiamiFulfillment, resolveShoppingMarket } from '../../../utilities/fulfillmentMarket'

const AddUnits = dynamic(() => import('../../AddUnits'), { suspense: true })

const parseSnippet = snippet => {
  const [label, value] = String(snippet || '').split('//')
  return { label, value }
}

const ProductRow = ({
  product: p,
  addToCartItems,
  openRentalStart,
  unifiedEligible = false,
  catalogId,
  listName,
  listPosition = 0,
  resultCount,
  activeFilter = '',
  sort = 'Relevance',
  actionMode = 'rental',
  supportingLabel = 'Supporting system component',
  supportingDescription = 'This item is selected as part of a complete, professionally installed LED video wall.',
  supportingActionLabel = 'View specifications'
}) => {
  const [expanded, setExpanded] = useState(false)
  const dispatch = useDispatch()
  const { pathname, push } = useRouter()
  const workingPackage = useSelector(getWorkingPackage)
  const order = useSelector(getWorkingOrder)
  const { cart, user } = useSelector(getUser)
  const geo = useSelector(getUserGeo)
  const purchaseOrder = order?.request_type === 'purchase'
  const shoppingMarket = resolveShoppingMarket({geo, order, pathname})
  const orlando = shoppingMarket === 'Orlando'
  const miamiFulfillment = orlando && requiresMiamiFulfillment(p)
  const orlandoAvailability = getBranchAvailability(p, 'Orlando')
  const miamiAvailability = getBranchAvailability(p, 'Miami')
  const description = p?.description?.trim() || ''
  const productUrl = `/products/${helper.makeUrl(p.name)}`
  const estimatedDelivery = p?.price?.rental * ((p?.difficulty || 0) / 10) * 1.35
  const baseDeliveryMinimum = p.requires_two ? 240 : 120
  const deliveryStartingPrice = Math.ceil(Math.max(estimatedDelivery || 0, baseDeliveryMinimum))
  const activeCartItem = cart.items?.find(item => item.product.name === p?.name)
  const displayPrice = p.only_purchase || (purchaseOrder && p?.price?.map)
    ? (p.price?.map || p.price?.purchase)
    : p.price?.rental
  const priceLabel = p.only_purchase || (purchaseOrder && p?.price?.map)
    ? 'Purchase price'
    : 'Rental price · 24 hours'
  const pickupAvailable = p?.allow_pickup === true
  const fulfillmentLabel = pickupAvailable
    ? 'Pickup or delivery · live availability after dates'
    : 'Delivery & setup only · live availability after dates'
  const details = expanded ? description : helper.shortText(description.split('\n')?.[0], 32)
  // A product can appear in more than one catalog on the same page. Include the
  // catalog instance so its description and disclosure control remain a unique
  // ARIA pair in the document.
  const productDescriptionKey = helper.makeUrl(String(p?._id || p?.name || 'product'))
  const descriptionId = `${catalogId || 'product-catalog'}-${productDescriptionKey}-description`
  const trackingContext = {
    filterState: activeFilter || 'all',
    itemIndex: listPosition,
    itemListId: catalogId,
    itemListName: listName,
    productId: p?._id,
    productName: p?.name,
    resultCount,
    sortState: sort,
    sourceSurface: `product_catalog:${listName}`
  }

  const trackView = () => {
    gtag.event({
      action: 'select_item',
      label: 'sales',
      category: 'orders',
      currency: 'USD',
      ...trackingContext,
      items: [{
        item_id: p?._id,
        item_name: p?.name,
        index: listPosition,
        item_category: p.categories?.[0]?.split('/')?.[0],
        item_category2: p.categories?.[0]?.split('/')?.[1],
        price: p?.price?.rental,
        quantity: 1
      }]
    })
  }

  const addPickup = () => {
    if (p.only_purchase && !order?._id) {
      return dispatch(purchaseAddItem({ product: p, units: 1, push, orlando, user, pathname }))
    }
    return addToCartItems(p)
  }

  const chooseDates = event => {
    event.preventDefault()
    openRentalStart?.(p, trackingContext)
  }

  return (
    <article className={styles.row} data-catalog-product-id={p._id}>
      <Action
        href={productUrl}
        variant='plain'
        wrapContent={false}
        className={styles.mediaLink}
        onClick={trackView}
        ariaLabel={`View ${p.name} details`}
        ariaHidden='true'
        tabIndex={-1}
        dataAction='view-product'
        dataCatalogId={catalogId}
        dataProductId={p?._id}
      >
        <Media
          picture={p.picture}
          alt={p.name}
          policy={MEDIA_POLICY_KEYS.THUMBNAIL}
          ratio='1 / 1'
        />
      </Action>

      <div className={styles.information}>
        <Action
          href={productUrl}
          variant='plain'
          wrapContent={false}
          className={styles.name}
          onClick={trackView}
          dataAction='view-product'
          dataCatalogId={catalogId}
          dataProductId={p?._id}
        >
          {p.name}
        </Action>
        {details && <p className={expanded ? styles.detailsExpanded : ''} id={descriptionId}>{details}</p>}
        {(description.split(' ').length > 32 || description.includes('\n')) && (
          <Action
            variant='plain'
            wrapContent={false}
            className={styles.expand}
            ariaExpanded={expanded}
            ariaControls={descriptionId}
            ariaLabel={`${expanded ? 'Show less about' : 'View more about'} ${p.name}`}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? <FiChevronUp aria-hidden='true' /> : <FiChevronDown aria-hidden='true' />}
            {expanded ? 'Show less' : 'View more'}
          </Action>
        )}
        {p.snippets?.length > 0 && (
          <ul className={styles.attributes} aria-label={`${p.name} key attributes`}>
            {p.snippets.slice(0, 4).map(snippet => {
              const { label, value } = parseSnippet(snippet)
              if (!label || !value) return null
              return (
                <li key={snippet}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className={styles.purchase}>
        {actionMode === 'details' ? (
          <div className={styles.supportingInventory}>
            <span>{supportingLabel}</span>
            <p>{supportingDescription}</p>
            <Action
              href={productUrl}
              variant='secondary'
              size='compact'
              icon={FiArrowRight}
              onClick={trackView}
              dataAction='view-supporting-component'
              dataCatalogId={catalogId}
              dataProductId={p?._id}
            >
              {supportingActionLabel}
            </Action>
          </div>
        ) : <>
        <div className={styles.price}>
          <span>{priceLabel}</span>
          <strong>{helper.formatPrice(displayPrice)}</strong>
        </div>
        {!p.only_purchase && (!purchaseOrder || !p?.price?.map) && (
          <div className={styles.delivery}>
            <span>Delivery &amp; setup from</span>
            <strong>{helper.formatPrice(deliveryStartingPrice)} per order</strong>
            <small>Final price uses your dates, location and complete order</small>
          </div>
        )}
        {miamiFulfillment && (
          <p className={styles.warning}>
            <FiAlertTriangle aria-hidden='true' />
            <span>
              <strong>Miami warehouse inventory</strong>
              Adding this item changes fulfillment to Miami and may add travel.
              {miamiAvailability !== null && ` ${miamiAvailability} available for these dates.`}
            </span>
          </p>
        )}
        {orlando && !miamiFulfillment && orlandoAvailability !== null && (
          <p className={styles.localAvailability}>Orlando warehouse · {orlandoAvailability} available</p>
        )}

        {workingPackage ? (
          <div className={styles.productUnits}>
            <Suspense fallback={<Spinner size='tiny' />}>
              <AddUnits
                bigButton
                subCat
                pack
                ui='v2'
                selectedUnits={workingPackage.components.find(item => item.product.name === p?.name)?.units || 0}
                product={p}
              />
            </Suspense>
          </div>
        ) : cart.items?.length ? (
          purchaseOrder ? (
            <Action
              icon={FiShoppingCart}
              disabled={!pickupAvailable || miamiFulfillment}
              onClick={() => (p.only_purchase || p.price.map)
                ? dispatch(purchaseAddItem({ product: p, units: 1, push, orlando, user, pathname }))
                : addToCartItems(p)}
              size='compact'
            >
              {!pickupAvailable || miamiFulfillment
                ? 'Delivery required'
                : (p.only_purchase || p.price.map) ? 'Purchase Product' : 'Rent Product'}
            </Action>
          ) : (
            <div className={styles.actionGrid}>
              <Action
                icon={FiTruck}
                variant='secondary'
                size='compact'
                ariaLabel={`Switch this rental to delivery and add ${p.name}`}
                onClick={() => addToCartItems(p, true)}
              >
                Use delivery
              </Action>
              <div className={styles.productUnits}>
                <Suspense fallback={<Spinner size='tiny' />}>
                  <AddUnits
                    bigButton
                    ui='v2'
                    selectedUnits={activeCartItem?.units || 0}
                    product={p}
                  />
                </Suspense>
              </div>
            </div>
          )
        ) : p.only_purchase ? (
          <div className={styles.actionGrid}>
            <Action icon={FiTruck} variant='secondary' size='compact' onClick={() => addToCartItems(p, true)}>
              {miamiFulfillment ? 'Deliver from Miami' : 'Schedule Delivery'}
            </Action>
            <Action
              icon={FiShoppingCart}
              disabled={!pickupAvailable || miamiFulfillment}
              onClick={addPickup}
              size='compact'
            >
              {!pickupAvailable || miamiFulfillment ? 'Delivery required' : 'Purchase Product'}
            </Action>
          </div>
        ) : (
          <div className={styles.entryAction}>
            <span>{fulfillmentLabel}</span>
            <Action
              href={`${productUrl}#rent-online`}
              icon={FiCalendar}
              onClick={chooseDates}
              size='compact'
              ariaLabel={`Choose Dates & Rent Online for ${p.name}`}
              dataAction='start-rental'
              dataCatalogId={catalogId}
              dataProductId={p?._id}
            >
              Choose Dates &amp; Rent Online
            </Action>

            {/* Legacy two-CTA entry retained for reference while the new entry is tested.
            <div className={styles.actionGrid}>
              <Action icon={FiTruck} variant='secondary' size='compact' onClick={() => addToCartItems(p, true)}>
                {!p.orlando && orlando ? 'Ship from Miami' : unifiedEligible ? 'Add with delivery & setup' : 'Schedule Delivery'}
              </Action>
              <Action
                icon={FiShoppingCart}
                disabled={!p.allow_pickup || (!p.orlando && orlando)}
                onClick={addPickup}
                size='compact'
              >
                {!p.allow_pickup || (!p.orlando && orlando)
                  ? 'Pickup unavailable'
                  : unifiedEligible ? 'Add for warehouse pickup' : 'Add to Rental'}
              </Action>
            </div>
            */}
          </div>
        )}
        </>}
      </div>
    </article>
  )
}

export default ProductRow
